import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminMarketing.css';

const EMPTY_FORM = {
    name: '',
    target_url: '/',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    notes: '',
    is_active: true,
};

// Пресеты закрывают типовые каналы — руками остаётся только кампания
const PRESETS = [
    { label: 'Яндекс.Директ', utm_source: 'yandex', utm_medium: 'cpc' },
    { label: 'VK Реклама', utm_source: 'vk', utm_medium: 'cpc' },
    { label: 'Telegram-канал', utm_source: 'telegram', utm_medium: 'social' },
    { label: 'Email-рассылка', utm_source: 'email', utm_medium: 'email' },
    { label: 'Блог / статья', utm_source: 'blog', utm_medium: 'content' },
    { label: 'Партнёр', utm_source: 'partner', utm_medium: 'referral' },
    { label: 'Офлайн / QR', utm_source: 'offline', utm_medium: 'qr' },
];

function AdminMarketingLinks() {
    const { request } = useApi();
    const [links, setLinks] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set('search', search.trim());
            const data = await request(`/api/v1/admin/marketing/links?${params.toString()}`);
            setLinks(Array.isArray(data) ? data : []);
        } catch (error) {
            logger.error('Не удалось загрузить UTM-ссылки', error);
            setMessage({ type: 'error', text: 'Не удалось загрузить список ссылок' });
        } finally {
            setLoading(false);
        }
    }, [request, search]);

    useEffect(() => {
        load();
    }, [load]);

    const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const applyPreset = (preset) => {
        setForm((prev) => ({ ...prev, utm_source: preset.utm_source, utm_medium: preset.utm_medium }));
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!form.name.trim() || !form.target_url.trim()) {
            setMessage({ type: 'error', text: 'Заполните название и целевой URL' });
            return;
        }
        setLoading(true);
        setMessage(null);
        try {
            const path = editingId
                ? `/api/v1/admin/marketing/links/${editingId}`
                : '/api/v1/admin/marketing/links';
            const created = await request(path, {
                method: editingId ? 'PUT' : 'POST',
                body: JSON.stringify(form),
            });
            setMessage({
                type: 'ok',
                text: editingId ? 'Ссылка обновлена' : `Ссылка создана: ${created?.short_url || ''}`,
            });
            resetForm();
            await load();
        } catch (error) {
            logger.error('Не удалось сохранить ссылку', error);
            setMessage({ type: 'error', text: error?.message || 'Не удалось сохранить ссылку' });
        } finally {
            setLoading(false);
        }
    };

    const editLink = (link) => {
        setEditingId(link.id);
        setForm({
            name: link.name || '',
            target_url: link.target_url || '',
            utm_source: link.utm_source || '',
            utm_medium: link.utm_medium || '',
            utm_campaign: link.utm_campaign || '',
            utm_content: link.utm_content || '',
            utm_term: link.utm_term || '',
            notes: link.notes || '',
            is_active: link.is_active,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const removeLink = async (link) => {
        if (!window.confirm(`Удалить ссылку «${link.name}»? История кликов будет удалена.`)) {
            return;
        }
        try {
            await request(`/api/v1/admin/marketing/links/${link.id}`, { method: 'DELETE' });
            await load();
        } catch (error) {
            logger.error('Не удалось удалить ссылку', error);
            setMessage({ type: 'error', text: 'Не удалось удалить ссылку' });
        }
    };

    const copy = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            setMessage({ type: 'ok', text: 'Скопировано' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Буфер обмена недоступен' });
        }
    };

    return (
        <div className="mkt-page">
            <h1>UTM-генератор</h1>
            <p className="mkt-page__hint">
                Ссылка сокращается до вида <span className="mkt-code">seller.anylex.ru/abcd2345</span> — метки остаются
                внутри и подставляются при переходе. Клики и регистрации по каждой ссылке видны в таблице ниже
                и в разделе «Источники».
            </p>

            <form className="mkt-card" onSubmit={submit}>
                <div className="mkt-card__title">{editingId ? 'Редактирование ссылки' : 'Новая ссылка'}</div>

                <div className="mkt-tabs">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            className="mkt-tab"
                            onClick={() => applyPreset(preset)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>Название (для себя)</span>
                        <input
                            value={form.name}
                            onChange={(e) => setField('name', e.target.value)}
                            placeholder="Директ — поиск, сентябрь"
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Куда ведёт (URL или путь)</span>
                        <input
                            value={form.target_url}
                            onChange={(e) => setField('target_url', e.target.value)}
                            placeholder="/blog/kak-schitat-drr"
                        />
                    </label>
                    <label className="mkt-field">
                        <span>utm_source</span>
                        <input value={form.utm_source} onChange={(e) => setField('utm_source', e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>utm_medium</span>
                        <input value={form.utm_medium} onChange={(e) => setField('utm_medium', e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>utm_campaign</span>
                        <input value={form.utm_campaign} onChange={(e) => setField('utm_campaign', e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>utm_content</span>
                        <input value={form.utm_content} onChange={(e) => setField('utm_content', e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>utm_term</span>
                        <input value={form.utm_term} onChange={(e) => setField('utm_term', e.target.value)} />
                    </label>
                    <label className="mkt-field">
                        <span>Заметка</span>
                        <input value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
                    </label>
                </div>

                <div className="mkt-actions">
                    <button type="submit" disabled={loading}>
                        {editingId ? 'Сохранить' : 'Создать ссылку'}
                    </button>
                    {editingId && (
                        <button type="button" onClick={resetForm}>
                            Отмена
                        </button>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={(e) => setField('is_active', e.target.checked)}
                        />
                        Активна
                    </label>
                </div>

                {message && (
                    <div className={`mkt-message mkt-message--${message.type === 'error' ? 'error' : 'ok'}`}>
                        {message.text}
                    </div>
                )}
            </form>

            <div className="mkt-card">
                <div className="mkt-actions" style={{ marginTop: 0, marginBottom: 12 }}>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Поиск по названию, коду, кампании"
                        style={{ flex: 1, minWidth: 220, padding: '8px 10px' }}
                    />
                    <button type="button" onClick={load} disabled={loading}>
                        Обновить
                    </button>
                </div>

                {links.length === 0 ? (
                    <div className="mkt-empty">{loading ? 'Загрузка…' : 'Ссылок пока нет'}</div>
                ) : (
                    <table className="mkt-table">
                        <thead>
                            <tr>
                                <th>Название</th>
                                <th>Короткая ссылка</th>
                                <th>Метки</th>
                                <th className="num">Кликов (30 дн.)</th>
                                <th className="num">Всего</th>
                                <th className="num">Регистраций</th>
                                <th>Статус</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {links.map((link) => (
                                <tr key={link.id}>
                                    <td>
                                        {link.name}
                                        <div className="mkt-code" style={{ opacity: 0.6 }}>{link.target_url}</div>
                                    </td>
                                    <td>
                                        <span className="mkt-code">{link.short_url}</span>
                                        <div>
                                            <button type="button" onClick={() => copy(link.short_url)}>
                                                Копировать
                                            </button>
                                        </div>
                                    </td>
                                    <td className="mkt-code">
                                        {[link.utm_source, link.utm_medium, link.utm_campaign]
                                            .filter(Boolean)
                                            .join(' / ') || '—'}
                                    </td>
                                    <td className="num">{link.clicks_period}</td>
                                    <td className="num">{link.click_count}</td>
                                    <td className="num">{link.signups}</td>
                                    <td>
                                        <span className={`mkt-status mkt-status--${link.is_active ? 'on' : 'off'}`}>
                                            {link.is_active ? 'активна' : 'выключена'}
                                        </span>
                                    </td>
                                    <td>
                                        <button type="button" onClick={() => editLink(link)}>Изменить</button>
                                        <button type="button" onClick={() => removeLink(link)}>Удалить</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default AdminMarketingLinks;
